import lodash from 'lodash';
import type { Ref } from 'react';
import React, { forwardRef } from 'react';

import type { WixRicosEditorProps } from './../wix-ricos-common';
import {
  experimentStrategy,
  extractPluginsConfig,
  runStrategiesWithCommonUtils,
  themeStrategy,
} from './../wix-ricos-common';
import type { RichContentEditorProps } from './../ricos-common';
import type { RicosEditorType } from './../ricos-editor';
import { RicosEditor } from './../ricos-editor';
import { isEditorPlugin } from './../ricos-editor/utils/is-editor-plugin';
import { RICOS_VERSION } from './../ricos-schema/next-schema';
import { useGetWixClientPromise } from './../_shared/request-service-provider/use-get-wix-client-promise';

import { runEditorStrategies } from './runEditorStrategies';

type Experiment = {
  enabled: boolean;
  value?: string;
  namespace?: string;
};

const WixRicosEditor = (props: WixRicosEditorProps, ref: Ref<RicosEditorType>) => {
  const {
    children,
    biSettings,
    instance,
    plugins,
    theme,
    wixExperiments = {},
    visitorUploads,
    msid,
    fileUploadSizeLimit,
    ...rest
  } = props;

  const experiments = experimentStrategy(wixExperiments);
  const { getWixClientPromise } = useGetWixClientPromise(instance);

  const editorPlugins = React.useMemo(() => plugins?.filter(isEditorPlugin), [plugins]);

  const { strategies } = runStrategiesWithCommonUtils({
    plugins: editorPlugins,
    instance,
    biSettings,
    wixRicosVersion: RICOS_VERSION,
    ricosVersion: RICOS_VERSION,
    experiments,
    getWixClientPromise,
    msid,
  });

  const _rcProps: RichContentEditorProps & { experiments: Record<string, Experiment> } =
    lodash.merge(
      strategies,
      runEditorStrategies(
        editorPlugins,
        instance,
        getWixClientPromise,
        biSettings,
        visitorUploads,
        fileUploadSizeLimit
      ),
      extractPluginsConfig(editorPlugins),
      { getWixClientPromise }
    );

  const isBmPlatform = typeof window !== 'undefined' && !!window?.ModuleRegistry;

  return (
    <RicosEditor
      _rcProps={_rcProps}
      experiments={_rcProps.experiments}
      ref={ref}
      theme={themeStrategy(theme, isBmPlatform)}
      plugins={plugins}
      environment={isBmPlatform ? 'businessManager' : 'liveSite'}
      {...rest}
    >
      {children}
    </RicosEditor>
  );
};

export default forwardRef(WixRicosEditor);
